import type { TabContext } from './bridge';
import { hubspotRecordFromUrl, hubspotRecordPath } from './hubspot-record-id';

/** Chrome Web Store id of shipped Cleanlist 0.3.4. Extension ids are public. */
export const PRODUCTION_EXTENSION_ID =
  import.meta.env.VITE_PRODUCTION_EXTENSION_ID ??
  'njbepggbhklfpljfcmpnonjdolibjadj';

/** Edge Add-ons assigns its own id (Partner Center → Extension identity →
 * CRX ID). Edge still serves extension pages from chrome-extension://. */
export const EDGE_EXTENSION_ID = 'bnealiefoejkknofehgjpnlbpdkmlofm';

export const DEVELOPMENT_EXTENSION_ID = import.meta.env
  .VITE_DEVELOPMENT_EXTENSION_ID;

export const extensionOrigin = (id: string): string =>
  `chrome-extension://${id}`;

export const isAllowedExtensionOrigin = (
  origin: string,
  development = import.meta.env.DEV
): boolean =>
  origin === extensionOrigin(PRODUCTION_EXTENSION_ID) ||
  origin === extensionOrigin(EDGE_EXTENSION_ID) ||
  (development && origin === extensionOrigin(DEVELOPMENT_EXTENSION_ID));

export const isTrustedExtensionMessage = (
  event: Pick<MessageEvent, 'origin' | 'source'>,
  parent: MessageEventSource,
  development = import.meta.env.DEV
): boolean =>
  event.source === parent &&
  isAllowedExtensionOrigin(event.origin, development);

export const isPortalDeepLink = (url: string, portalUrl: string): boolean => {
  try {
    return new URL(url).origin === new URL(portalUrl).origin;
  } catch {
    return false;
  }
};

const canonicalLinkedInIdentitySegment = (raw: string): string | null => {
  // URL.pathname retains percent escapes. Decode to a fixed point so mixed
  // forms such as `%25%32%46` cannot reveal a separator only after a later
  // consumer decodes the identity again. Cap nesting and fail closed if it
  // never stabilizes or any layer is malformed.
  let decoded = raw;
  for (let depth = 0; depth < 6; depth += 1) {
    let next: string;
    try {
      next = decodeURIComponent(decoded);
    } catch {
      return null;
    }
    if (next.includes('/') || next.includes(',')) return null;
    if (next === decoded) return encodeURIComponent(next);
    decoded = next;
  }
  return null;
};

const linkedinIdentityPath = (pathname: string): string | null => {
  const profile = pathname.match(/^\/in\/([^/,]+)(?:\/|$)/i);
  if (profile) {
    const identity = canonicalLinkedInIdentitySegment(profile[1]);
    return identity ? `/in/${identity}` : null;
  }

  const salesProfile = pathname.match(
    /^\/sales\/(lead|people)\/([^/,]+)(?:[,/]|$)/i
  );
  if (!salesProfile) return null;
  const identity = canonicalLinkedInIdentitySegment(salesProfile[2]);
  return identity
    ? `/sales/${salesProfile[1].toLowerCase()}/${identity}`
    : null;
};

/** Salesforce key prefixes the panel has a surface for. */
const SF_OBJECT_BY_PREFIX: Record<string, string> = {
  '001': 'Account',
  '003': 'Contact',
  '00q': 'Lead',
};

// The object name comes from the id prefix, not the URL, so the path is always
// canonical and a crafted Contact/001 pair cannot pass through.
const salesforceRecordPath = (pathname: string): string | null => {
  const lightning = pathname.match(
    /^\/lightning\/r\/(?:Account|Contact|Lead)\/((001|003|00Q)[a-z0-9]{12}(?:[a-z0-9]{3})?)(?:\/|$)/i
  );
  if (lightning) {
    const object = SF_OBJECT_BY_PREFIX[lightning[2].toLowerCase()];
    return object ? `/lightning/r/${object}/${lightning[1]}/view` : null;
  }

  const classic = pathname.match(
    /^\/((?:001|003|00Q)[a-z0-9]{12}(?:[a-z0-9]{3})?)(?:\/|$)/i
  );
  return classic ? `/${classic[1]}` : null;
};

/** Defence in depth for older shell builds that still send full tab URLs. */
export const minimizeRemoteContext = (context: TabContext): TabContext => {
  if (!context.url || context.surface === 'restricted')
    return { ...context, url: '' };
  try {
    const parsed = new URL(context.url);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:')
      return { ...context, url: '' };
    const hostname = parsed.hostname.toLowerCase();
    const isLinkedInHost =
      hostname === 'linkedin.com' || hostname === 'www.linkedin.com';
    const isHostOrSubdomain = (suffix: string): boolean =>
      hostname === suffix || hostname.endsWith(`.${suffix}`);
    const isSalesforceHost =
      isHostOrSubdomain('my.salesforce.com') ||
      isHostOrSubdomain('lightning.force.com');
    const hubspotRecord = hubspotRecordFromUrl(context.url);
    const identityPath = hubspotRecord
      ? hubspotRecordPath(hubspotRecord)
      : isLinkedInHost
        ? linkedinIdentityPath(parsed.pathname)
        : isSalesforceHost
          ? salesforceRecordPath(parsed.pathname)
          : null;
    return {
      ...context,
      url: identityPath ? `${parsed.origin}${identityPath}` : parsed.origin,
    };
  } catch {
    return { ...context, url: '' };
  }
};

const SENSITIVE_KEY =
  /^(authorization|cookie|set-cookie|token|access[_-]?token|refresh[_-]?token|id[_-]?token|csrf[_-]?token|sec-websocket-protocol)$/i;

const redactText = (value: string): string =>
  value
    // These headers may contain multiple comma/semicolon-delimited opaque
    // credentials. Redact the complete value before generic token matching
    // can replace only the first protocol/cookie fragment.
    .replace(
      /(\bsec-websocket-protocol\s*["']?\s*[:=]\s*["']?)[^\r\n]+/gi,
      '$1[REDACTED]'
    )
    .replace(
      /(\b(?:set-cookie|cookie)\s*["']?\s*[:=]\s*["']?)[^\r\n]+/gi,
      '$1[REDACTED]'
    )
    .replace(/(bearer\s+)[a-z0-9._~+/=-]+/gi, '$1[REDACTED]')
    .replace(/\beyJ[a-z0-9_-]+\.[a-z0-9_-]+\.[a-z0-9_-]+\b/gi, '[REDACTED]')
    .replace(
      /((?:authorization|csrf[_-]?token|access[_-]?token|refresh[_-]?token|id[_-]?token|token)\s*["']?\s*[:=]\s*["']?)[^\s,"';}\]]+/gi,
      '$1[REDACTED]'
    );

export const redactSensitive = (value: unknown, depth = 0): unknown => {
  if (typeof value === 'string') return redactText(value);
  if (value == null || typeof value === 'number' || typeof value === 'boolean')
    return value;
  if (depth >= 4) return '[TRUNCATED]';
  if (value instanceof Error)
    return {
      name: value.name,
      message: redactText(value.message),
      stack: value.stack ? redactText(value.stack) : undefined,
    };
  if (Array.isArray(value))
    return value.map((item) => redactSensitive(item, depth + 1));
  if (typeof value === 'object') {
    const output: Record<string, unknown> = {};
    for (const [key, item] of Object.entries(value))
      output[key] = SENSITIVE_KEY.test(key)
        ? '[REDACTED]'
        : redactSensitive(item, depth + 1);
    return output;
  }
  return redactText(String(value));
};

export const safeErrorMessage = (error: unknown): string =>
  redactText(error instanceof Error ? error.message : String(error));
